<template>
  <div class="row">
    <div class="col-md-12">
      <create-edit-end-customer-form v-if="showForm"
        :edit-mode="editMode"
        :require-registry-verification="requireRegistryVerification"
        :editing-end-customer-id="editingEndCustomerId"
        :name-prop="editingName"
        :address-prop="editingAddress"
        :email-prop="editingEmail"
        :identification-number-prop="editingIdentificationNumber"
        @close="closeForm"
        @refresh-data="fetchData">
      </create-edit-end-customer-form>
    </div>
    <div class="col-12">
      <card
        :title="$t('endCustomers.endCustomers')"
        :on-add-object="addMethod"
        :on-custom-method="syncMethod"
        custom-method-icon="sync"
        :custom-method-image="getERPImage"
        :custom-method-icon-spin="loading"
      >
        <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mb-2">
          <div class="col-12 col-md-4 col-sm-6 mb-2">
            <el-input
              v-model="searchQuery"
              class="search-input"
              clearable
              :prefix-icon="Search"
              :placeholder="$t('common.searchRecords')"
              aria-controls="datatables"
              @keyup="search"
              @clear="handleClearSearch"
            />
          </div>
        </div>

        <paginated-table
          :columns="tableColumns"
          :data="endCustomers"
          :pagination="pagination"
          :ready="dataReady"
          :allow-click="false"
          :enable-edit="isAdmin"
          :enable-delete="isAdmin"
          @update-page="updatePage"
          @edit="handleEdit"
          @delete="handleDelete"
        />
      </card>
    </div>
  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { loadCustomerMeCached } from '../../services/customerMeHelper';
import { ElInput } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Card from "../../components/Card.vue";
import PaginatedTable from "../../components/PaginatedTable.vue";
import CreateEditEndCustomerForm from "./CreateEditEndCustomerForm.vue";
import swal from 'sweetalert2';

export default {
  components: {
    ElInput,
    Card,
    PaginatedTable,
    CreateEditEndCustomerForm,
  },
  data() {
    return {
      Search: shallowRef(Search),
      customerId: '',
      erpName: '',
      loading: false,
      userRole: '',
      dataReady: false,
      showForm: false,
      editMode: false,
      editingEndCustomerId: null,
      editingName: '',
      editingAddress: '',
      editingEmail: '',
      editingIdentificationNumber: '',
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      searchQuery: '',
      endCustomers: [],
      tableColumns: [
        { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
        { prop: 'address', label: this.$t('endCustomers.address'), minWidth: 200 },
        { prop: 'email', label: this.$t('common.email'), minWidth: 200 },
        { prop: 'identificationNumber', label: this.$t('endCustomers.identificationNumber'), minWidth: 150 },
        { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.name': 'name', 'endCustomers.address': 'address', 'common.email': 'email', 'endCustomers.identificationNumber': 'identificationNumber' } },
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'Admin' || this.userRole === 'SuperAdmin';
    },
    syncMethod() {
      if (!this.isAdmin) return this.dummyMethod;
      return this.sync;
    },
    addMethod() {
      if (!this.isAdmin) return null;
      return this.openCreateForm;
    },
    requireRegistryVerification() {
      return this.erpName === 'BusinessCentralEndCustomers';
    },
    getERPImage() {
      switch (this.erpName) {
        case 'DK': case 'DKProjectRequests': case 'DKFinnur': case 'DKWithProjectPhases': return 'img/dk-logo2.png';
        case 'Tímon': return 'img/timon.png';
        case 'Stólpi': return 'img/stolpi.png';
        case 'BusinessCentralCustomers': case 'BusinessCentralEndCustomers': return 'img/business-central-logo.png';
        case 'ReglaIs': return 'img/Regla_logo.png';
        case 'Verklag': return 'img/verklag_logo.png';
        default: return '';
      }
    },
  },
  created() {
    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;
    this.customerId = decodedToken.CustomerId;

    // Get ERP
    if (Cookies.get(this.customerId + '-erpName')) {
      this.erpName = Cookies.get(this.customerId + '-erpName');
    } else {
      loadCustomerMeCached()
        .then(customer => {
          if (customer) {
            Cookies.set(this.customerId + '-erpName', customer.erpName);
            this.erpName = customer.erpName;
          } else {
            Cookies.set(this.customerId + '-erpName', '');
          }
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
        });
    }

    getCustomerConfig('DefaultPageSize').then(config => {
      this.pagination.perPage = config === '' ? 10 : config;
      this.fetchData();
    });
  },
  methods: {
    dummyMethod() {},

    search(event) {
      this.pagination.currentPage = 1;
      const queryLength = this.searchQuery.length;
      if (event.key === 'Backspace' || event.key === 'Enter') {
        this.fetchData();
      } else if (queryLength > 1 && queryLength % 2 === 0) {
        this.fetchData();
      }
    },

    updatePage(page) {
      this.pagination.currentPage = page;
      this.fetchData();
    },

    handleClearSearch() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    openCreateForm() {
      this.editMode = false;
      this.editingEndCustomerId = null;
      this.editingName = '';
      this.editingAddress = '';
      this.editingEmail = '';
      this.editingIdentificationNumber = '';
      this.showForm = true;
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },

    closeForm() {
      this.showForm = false;
      this.editMode = false;
      this.editingEndCustomerId = null;
    },

    handleEdit(index, row) {
      this.editMode = true;
      this.editingEndCustomerId = row.id;
      this.editingName = row.name ?? '';
      this.editingAddress = row.address ?? '';
      this.editingEmail = row.email ?? '';
      this.editingIdentificationNumber = row.identificationNumber ?? '';
      this.showForm = true;
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },

    handleDelete(index, row) {
      swal.fire({
        title: this.$t('delete.areYouSure'),
        text: this.$t('endCustomers.areYouSureDelete'),
        icon: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
          cancelButton: 'btn btn-danger btn-fill'
        },
        confirmButtonText: this.$t('delete.yesDeleteIt'),
        cancelButtonText: this.$t('delete.cancel'),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteEndCustomer(row);
        }
      });
    },

    deleteEndCustomer(row) {
      api.delete(`/EndCustomer/${row.id}`)
        .then(() => {
          this.$toast.success(this.$t('endCustomers.endCustomerDeleted'), { timeout: 2000 });
          this.fetchData();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          const data = error.response?.data;
          const message = data?.detail || data?.title || this.$t('common.somethingWentWrong');
          this.$toast.danger(message, { timeout: 0 });
        });
    },

    fetchData() {
      this.dataReady = false;
      let url = `/EndCustomer?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}`;
      if (this.searchQuery !== '') {
        url += `&name=${this.searchQuery}`;
      }
      api.get(url)
        .then(response => {
          this.pagination.total = response.data.totalItems;
          this.endCustomers = response.data.result.map(item => ({
            id: item.id,
            name: item.name,
            address: item.address,
            email: item.email,
            identificationNumber: item.identificationNumber,
          }));
          this.dataReady = true;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },

    sync() {
      this.loading = true;
      api.post('/EndCustomer/Sync')
        .then(response => {
          if (response.data.success) {
            this.$toast.success(
              `${response.data.endCustomersCreated} ${this.$t('endCustomers.endCustomersCreated')} - ${response.data.endCustomersUpdated} ${this.$t('endCustomers.endCustomersUpdated')}`,
              { timeout: 2000 }
            );
            this.fetchData();
          } else {
            this.$toast.danger(this.$t('common.somethingWentWrong') + ' - ' + response.data.errorMessage, { timeout: 0 });
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.loading = false;
          this.$toast.danger(this.$t('common.somethingWentWrong'), { timeout: 0 });
        });
    },
  },
};
</script>
